import React from 'react';
import { LEGAL_ENTITY, PRODUCT_NAME, CONTACT_EMAIL, WEBSITE_URL } from './constants';
import { getLegalStyles } from './legalStyles';

function MailLink({ styles }) {
    return (
        <a href={`mailto:${CONTACT_EMAIL}`} className={styles.link}>
            {CONTACT_EMAIL}
        </a>
    );
}

/**
 * Full Privacy Policy body. Used on /privacy-policy and in registration modals.
 * @param {{ variant?: 'page' | 'modal' }} props
 */
export function PrivacyPolicyContent({ variant = 'page' }) {
    const s = getLegalStyles(variant);

    return (
        <div className="space-y-8 leading-relaxed">
            <section className={s.section}>
                <h2 className={s.h2}>1. Introduction</h2>
                <p className={s.p}>
                    This Privacy Policy explains how <strong>{LEGAL_ENTITY}</strong> (“we”, “us”,
                    “our”) collects, uses, discloses, and protects personal information when you use{' '}
                    <strong>{PRODUCT_NAME}</strong> ({WEBSITE_URL}), including the merchant dashboard,
                    APIs, wallet passes, and related services (collectively, the “Service”).
                </p>
                <p className={s.p}>
                    {PRODUCT_NAME} is a digital loyalty platform that helps businesses (“Merchants”)
                    run loyalty programs, issue digital wallet passes, track visits and rewards, and
                    send marketing communications to their customers.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>2. Who This Policy Applies To</h2>
                <ul className={s.ul}>
                    <li className={s.li}>
                        <strong>Merchants</strong> — business users who register for and operate a{' '}
                        {PRODUCT_NAME} account (including owners and invited staff).
                    </li>
                    <li className={s.li}>
                        <strong>End customers</strong> — individuals who sign up for a Merchant’s
                        loyalty program (for example via a QR code or signup form) and receive a
                        digital wallet pass.
                    </li>
                    <li className={s.li}>
                        <strong>Website visitors</strong> — anyone who visits our marketing site or
                        support pages.
                    </li>
                </ul>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>3. Our Role vs. Merchant’s Role</h2>
                <p className={s.p}>
                    When a Merchant uploads or collects customer information through {PRODUCT_NAME},
                    the Merchant is generally the <strong>data controller</strong> (or “business”
                    under U.S. state privacy laws) for that customer data and is responsible for
                    providing its own privacy notice and obtaining any required consents.
                </p>
                <p className={s.p}>
                    {LEGAL_ENTITY} acts as a <strong>service provider / processor</strong> on the
                    Merchant’s instructions — we store and process customer data only to provide the
                    Service to that Merchant (for example loyalty tracking, pass updates, and
                    marketing sends the Merchant initiates).
                </p>
                <p className={s.p}>
                    For Merchant account data (your business login, billing, and settings),{' '}
                    {LEGAL_ENTITY} is the controller.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>4. Information We Collect</h2>
                <p className={s.p}>
                    <strong>Merchant account information:</strong> name, email address, password
                    (stored and authenticated securely), business name, phone
                    number, account settings, subscription and billing identifiers, staff invitations
                    and role assignments, pass branding (such as logos and colors), and communications
                    with our support team.
                </p>
                <p className={s.p}>
                    <strong>End customer information</strong> (provided by the customer or Merchant):
                    name, email address, phone number, birthday (month and day), device/platform
                    preference (e.g. iPhone or Android), marketing opt-in status, loyalty activity
                    (points, visits, scans, redemptions, rewards), wallet pass identifiers, and
                    related program history.
                </p>
                <p className={s.p}>
                    <strong>Automatically collected information:</strong> IP address, browser type,
                    device information, dates and times of access, pages or features used, API and
                    application logs, and similar technical data used for security, troubleshooting,
                    and analytics.
                </p>
                <p className={s.p}>
                    <strong>Payment information:</strong> paid subscriptions are processed through a
                    secure payment processor. We receive billing metadata (such as customer ID,
                    subscription status, and invoice references) but do not store full payment card
                    numbers on our servers.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>5. How We Use Information</h2>
                <p className={s.p}>We use personal information to:</p>
                <ul className={s.ul}>
                    <li className={s.li}>Provide, operate, and maintain the Service</li>
                    <li className={s.li}>
                        Create and update digital wallet passes and loyalty balances
                    </li>
                    <li className={s.li}>
                        Send transactional messages (e.g. pass links, staff invites, account
                        verification, password reset)
                    </li>
                    <li className={s.li}>
                        Deliver marketing email or SMS campaigns that a Merchant configures, subject
                        to opt-in and unsubscribe rules
                    </li>
                    <li className={s.li}>
                        Process subscriptions, enforce plan limits, and prevent fraud or abuse
                    </li>
                    <li className={s.li}>
                        Improve reliability, security, and product experience (including internal
                        analytics derived from usage logs)
                    </li>
                    <li className={s.li}>Comply with law and respond to lawful requests</li>
                </ul>
                <p className={s.p}>
                    We do <strong>not</strong> sell your personal information. We do not use end
                    customer data for our own unrelated advertising; Merchants control their
                    marketing content and audience.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>6. Legal Bases (EEA/UK Users)</h2>
                <p className={s.p}>
                    Where the GDPR or UK GDPR applies, we rely on: (a) <strong>contract</strong> — to
                    provide the Service you or your Merchant requested; (b){' '}
                    <strong>legitimate interests</strong> — security, fraud prevention, and product
                    improvement, balanced against your rights; and (c) <strong>consent</strong> — where
                    required for marketing messages or optional features. You may withdraw consent
                    where applicable without affecting prior processing.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>7. Marketing & Communications</h2>
                <p className={s.p}>
                    Merchants may send promotional email or SMS messages through {PRODUCT_NAME} to
                    customers who have opted in. Marketing emails include an unsubscribe mechanism
                    (one-click link) that updates the customer’s marketing preference for that
                    Merchant. Customers should contact the Merchant for questions about a specific
                    offer; you may also contact us at <MailLink styles={s} /> for platform-related
                    issues.
                </p>
                <p className={s.p}>
                    Merchants are solely responsible for complying with laws such as CAN-SPAM, TCPA,
                    and applicable state telemarketing rules when using messaging features.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>8. How We Share Information</h2>
                <p className={s.p}>
                    We do not sell personal information. We may share information only as needed to
                    operate the Service, including with:
                </p>
                <ul className={s.ul}>
                    <li className={s.li}>
                        Service providers that host our application, store data, process payments,
                        deliver email or SMS, or provide similar infrastructure — under contracts
                        that require confidentiality and limited use
                    </li>
                    <li className={s.li}>
                        Wallet and device platforms when a customer adds or uses a digital pass on
                        their phone
                    </li>
                    <li className={s.li}>
                        Professional advisers, regulators, or authorities when required by law or to
                        protect rights and safety
                    </li>
                    <li className={s.li}>
                        A successor entity in connection with a merger, acquisition, or sale of assets
                    </li>
                </ul>
                <p className={s.p}>
                    Merchants remain responsible for how they share customer data outside of{' '}
                    {PRODUCT_NAME}.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>9. Cookies & Local Storage</h2>
                <p className={s.p}>
                    The merchant application uses session and authentication mechanisms (including
                    secure session tokens) to keep you signed in. We may use browser local storage
                    for non-sensitive preferences (for example tutorial progress). We do not use
                    advertising cookies on the merchant dashboard. You can
                    clear cookies and site data through your browser settings; doing so may sign you
                    out.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>10. Data Retention</h2>
                <p className={s.p}>
                    We retain information for as long as needed to provide the Service, comply with
                    legal obligations, resolve disputes, and enforce agreements. Merchant account data
                    is kept while the account is active and for a reasonable period afterward.
                    Customer loyalty data is retained on behalf of the Merchant until the Merchant
                    deletes it or closes their account, subject to backup and legal hold periods.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>11. Security</h2>
                <p className={s.p}>
                    We use administrative, technical, and organizational safeguards appropriate to
                    the nature of the data (including access controls, encrypted connections, and
                    hosted infrastructure security). No method of transmission or storage is 100%
                    secure; we cannot guarantee absolute security.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>12. International Transfers</h2>
                <p className={s.p}>
                    We are based in the United States. If you access the Service from outside the
                    U.S., your information may be transferred to, stored in, and processed in the
                    U.S. and other countries where we or our service partners operate. Where required,
                    we implement
                    appropriate safeguards for cross-border transfers.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>13. Your Rights & Choices</h2>
                <p className={s.p}>
                    <strong>Merchants:</strong> You may access and update much of your account
                    information in Settings. To delete your merchant account, use the account
                    deletion request in Settings or email <MailLink styles={s} />. Deletion may be
                    subject to retention of billing records and legal requirements.
                </p>
                <p className={s.p}>
                    <strong>End customers:</strong> To access, correct, or delete loyalty data, or
                    to opt out of a Merchant’s marketing, contact the Merchant that operates the
                    program. You may use unsubscribe links in marketing emails. If the Merchant
                    cannot help you, contact us and we will assist in routing your request.
                </p>
                <p className={s.p}>
                    Depending on where you live, you may have additional rights (see Sections 14–15).
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>14. California Privacy Rights</h2>
                <p className={s.p}>
                    California residents may have rights to know, delete, and correct personal
                    information, and to opt out of “sale” or “sharing” (as defined by California law).
                    We do not sell personal information. To exercise rights, email{' '}
                    <MailLink styles={s} /> with “California Privacy Request” in the subject. We will
                    verify your request as required by law. You may designate an authorized agent with
                    written permission. We do not discriminate against you for exercising privacy
                    rights.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>15. Other U.S. State Privacy Rights</h2>
                <p className={s.p}>
                    Residents of states with comprehensive privacy laws (such as Virginia, Colorado,
                    Connecticut, and Utah) may have similar rights to access, delete, correct, and opt
                    out of certain processing. Contact <MailLink styles={s} /> to submit a request.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>16. Children’s Privacy</h2>
                <p className={s.p}>
                    The Service is intended for businesses and is not directed to children under 13
                    (or 16 where applicable). We do not knowingly collect personal information from
                    children. If you believe we have collected a child’s information, contact us and
                    we will delete it.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>17. Changes to This Policy</h2>
                <p className={s.p}>
                    We may update this Privacy Policy from time to time. We will post the revised
                    version with a new effective date. Material changes may be communicated by email
                    or in-app notice. Continued use after the effective date constitutes acceptance
                    of the updated policy, except where further consent is required by law.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>18. Contact Us</h2>
                <p className={s.p}>
                    Questions about this Privacy Policy or our data practices:
                </p>
                <p className={s.p}>
                    <strong>{LEGAL_ENTITY}</strong>
                    <br />
                    Email: <MailLink styles={s} />
                    <br />
                    Website:{' '}
                    <a href={WEBSITE_URL} className={s.link} target="_blank" rel="noopener noreferrer">
                        {WEBSITE_URL}
                    </a>
                </p>
            </section>

            <p className={s.disclaimer}>
                This policy is provided for general informational purposes and does not constitute
                legal advice. Consider consulting qualified counsel for your specific situation.
            </p>
        </div>
    );
}
